import { milkOptionLabel } from '@ordo/constants/milk-options'
import { specialDietStudentLabel } from '@ordo/constants/special-diet-student-label'
import { Button } from '@ordo/ui/button'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { toast } from '@ordo/ui/sonner'
import { useQueryClient } from '@tanstack/react-query'
import { LockKeyhole } from 'lucide-react'
import { useState } from 'react'

import { authHeaders } from '@/api/http'
import type { AdminSchool } from '@/api/school-orders'
import type { SpecialDietAccommodationWire } from '@/api/special-diet-accommodations'
import { type Mealtime, MEALTIME_LABELS } from '@/lib/mealtime'

import { AddNoteDialog, type AddNoteValues } from './special-diet-intake-dialog'

export function SpecialDietEditDialog({
  accommodation,
  school,
  revisionsEnabled,
}: {
  accommodation: SpecialDietAccommodationWire
  school: AdminSchool
  revisionsEnabled: boolean
}) {
  const [open, setOpen] = useState(false)
  const [amending, setAmending] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const editable = accommodation.status === 'PENDING_REVIEW'
  async function save(values: AddNoteValues) {
    setPending(true)
    setError(null)
    try {
      const response = await fetch(`/api/special-diet/accommodations/${encodeURIComponent(accommodation.id)}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expectedUpdatedAt: accommodation.updatedAt,
          action: amending ? 'AMEND' : 'UPDATE',
          resubmit: true,
          notes: values.notes,
          mealtimes: values.mealtimes,
          parentOrderingEnabled: values.parentOrderingEnabled,
          parentContactFirstName: values.parentContactFirstName,
          parentContactLastName: values.parentContactLastName,
          parentContactEmail: values.parentContactEmail,
          ...(values.note ? { note: values.note } : {}),
        }),
      })
      const result = await response.json()
      if (!response.ok) {
        throw new Error(result.message ?? 'Unable to resubmit this request.')
      }
      await queryClient.invalidateQueries({ queryKey: ['special-diet'] })
      toast.success(
        amending
          ? 'Revision submitted for dietitian review. The approved plan is unchanged.'
          : 'Request updated and sent to the same dietitian review.',
      )
      setOpen(false)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to resubmit this request.')
      throw error
    } finally {
      setPending(false)
    }
  }
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          setError(null)
          setAmending(false)
          setOpen(true)
        }}>
        {editable ? 'Edit request' : 'View request'}
      </Button>
      {open && (editable || amending) ? (
        <AddNoteDialog
          accommodation={accommodation}
          amendment={amending}
          school={school}
          open={open}
          onOpenChange={setOpen}
          pending={pending}
          error={error}
          onSubmit={save}
        />
      ) : null}
      {!editable && !amending && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{specialDietStudentLabel(accommodation)} · Special Diets</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <LockKeyhole className="size-4" />
                {accommodation.status === 'ACTIVE'
                  ? 'Approved by the dietitian. These settings are read-only.'
                  : 'This request is read-only.'}
              </p>
              {accommodation.status === 'ACTIVE' && !accommodation.successor && (
                <Button type="button" disabled={!revisionsEnabled} onClick={() => setAmending(true)}>
                  Amend request
                </Button>
              )}
              {accommodation.status === 'ACTIVE' && !accommodation.successor && !revisionsEnabled && (
                <p className="text-xs text-muted-foreground">
                  Amendments will be available once the updated review flow is enabled.
                </p>
              )}
              {accommodation.successor && (
                <p className="text-sm">
                  V{accommodation.successor.revision} already exists. Open that version from the Special Diets list.
                </p>
              )}
              <dl className="space-y-4 text-sm">
                <div>
                  <dt className="font-medium">Meals</dt>
                  <dd>{accommodation.mealtimes.map((meal) => MEALTIME_LABELS[meal as Mealtime] ?? meal).join(', ')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Parent ordering</dt>
                  <dd>{accommodation.parentOrderingEnabled ? 'Allowed' : 'School orders'}</dd>
                </div>
                {accommodation.restrictedAllergens.length > 0 && (
                  <div>
                    <dt className="font-medium">Foods excluded</dt>
                    <dd>{accommodation.restrictedAllergens.join(', ')}</dd>
                  </div>
                )}
                {accommodation.dietaryNeeds.length > 0 && (
                  <div>
                    <dt className="font-medium">Dietary needs</dt>
                    <dd>{accommodation.dietaryNeeds.join(', ')}</dd>
                  </div>
                )}
                {Boolean(accommodation.acceptableMilkOptionCodes?.length) && (
                  <div>
                    <dt className="font-medium">Approved milks</dt>
                    <dd>{accommodation.acceptableMilkOptionCodes!.map((code) => milkOptionLabel(code)).join(', ')}</dd>
                  </div>
                )}
                {accommodation.notes && (
                  <div>
                    <dt className="font-medium">Request details</dt>
                    <dd className="whitespace-pre-wrap">{accommodation.notes}</dd>
                  </div>
                )}
              </dl>
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
